/**
 * Split announcement text into plain runs and links, for React to render — never as
 * HTML. Three shapes become links: a full http(s) URL; an email address (mailto:); and
 * a bare leychoon.app or leychoon.com address ("namecard.leychoon.app"), which is how
 * people write our own apps. Anything else stays text, so a typo can't turn into a
 * link somewhere else.
 */
export type Run = { text: string; href?: string };

const LINK =
  /\bhttps?:\/\/[^\s<>"']+|[\w.+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}|(?<![\w.@-])(?:[a-z0-9-]+\.)*leychoon\.(?:app|com)(?!\.[a-z0-9])(?![\w-])(?:\/[^\s<>"']*)?/gi;
const EMAIL = /^[\w.+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}$/i;
/** Sentence punctuation that ends a link rather than belonging to it. */
const TRAILING = /[.,;:!?)\]]+$/;

export function linkify(text: string): Run[] {
  const runs: Run[] = [];
  let last = 0;
  for (const m of text.matchAll(LINK)) {
    let raw = m[0];
    const tail = TRAILING.exec(raw)?.[0] ?? "";
    raw = raw.slice(0, raw.length - tail.length);
    const start = m.index;
    if (start > last) runs.push({ text: text.slice(last, start) });
    if (EMAIL.test(raw)) {
      runs.push({ text: raw, href: `mailto:${raw}` });
      last = start + raw.length;
      continue;
    }
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    let safe: string | null = null;
    try {
      const url = new URL(href);
      if (url.protocol === "https:" || url.protocol === "http:") safe = url.href;
    } catch {
      safe = null;
    }
    runs.push(safe ? { text: raw, href: safe } : { text: raw });
    last = start + raw.length;
  }
  if (last < text.length) runs.push({ text: text.slice(last) });
  return runs.length > 0 ? runs : [{ text }];
}
